import React, {
  createContext,
  PropsWithChildren,
  useState,
  useEffect,
} from "react";
import { useErrorNotification } from "@common/components/Notification";
import { LocalReport } from "@services/reports/types";
import { getLocalReports } from "@services/reports";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  canViewAnalytics,
  canViewReports,
  hasLicense,
} from "@common/utils/user";

export type ReportState = {
  reports?: LocalReport[];
  loaded: boolean;
};

const defaultReportsContext = {
  reports: undefined,
  loaded: false,
};
export const ReportsContext = createContext<ReportState>(defaultReportsContext);

export const ReportsProvider = (props: PropsWithChildren<{}>) => {
  const [reports, setReports] = useState<LocalReport[]>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [user] = useAuthenticatedUser();
  const dispatchError = useErrorNotification();
  const accountCanViewReports = hasLicense(user?.account, "reports");
  const accountCanViewAnalytics = hasLicense(user?.account, "analytics");
  const canViewReport = accountCanViewReports && canViewReports(user);
  const canViewAnalyticsReports =
    accountCanViewAnalytics && canViewAnalytics(user);
  const fetch = async () => {
    try {
      if (canViewReport || canViewAnalyticsReports) {
        setReports(await getLocalReports());
      } else {
        setReports([]);
      }
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    if (reports === undefined) fetch();
    if (reports?.length == 0 && (canViewReport || canViewAnalyticsReports))
      fetch();
    if (reports) setLoaded(true);
  }, [reports, user]);

  return (
    <ReportsContext.Provider value={{ reports, loaded }}>
      {props.children}
    </ReportsContext.Provider>
  );
};
